import { useEffect, useId, useState } from 'react'
import Artwork from './Artwork.jsx'
import SheetClose from './SheetClose.jsx'
import Toggle from './Toggle.jsx'
import { ApiError, api } from '../lib/api.js'
import { useFocusTrap } from '../lib/focusTrap.js'
import {
  METHOD_LABELS,
  SCORE_MAX,
  SCORE_MIN,
  STATE_LABELS,
  SUBSCORE_MODE_HELP,
  SUBSCORE_MODE_LABELS,
  clamp,
  coverageLabel,
  dimensionLabel,
  display,
  familyLabel,
  parse,
} from '../lib/score.js'

/**
 * The rating control (DESIGN.md, MOCKUP `RatingSheet.jsx`).
 *
 * One component for three jobs — an album, a song, a track on an album —
 * because they are the same gesture with different amounts of context, and
 * two of them differing by a section is cheaper than two components that
 * drift. `variant` is what turns the extra sections on.
 *
 * The score is held as the integer the API stores and formatted at the edge,
 * so nothing here can introduce a value the server would refuse. Three ways in
 * on purpose: drag for approximately, step for exactly, type for exactly and
 * quickly — the last is also the only one a keyboard-only person finds fast.
 */

const DEFAULT_SCORE = 70
const METHODS = ['direct', 'track_average', 'blended']
const STATES = ['first_impression', 'developing', 'settled', 'retrospective']
const SUBSCORE_MODES = ['independent', 'auto_average', 'weighted']

// Where a dimension starts when somebody adds one, and where its weight
// starts. The weight default is 1 rather than 0 for the same reason the server
// treats an unset weight as 1: "unweighted" has to mean "counts once", never
// "does not count".
const DEFAULT_DIMENSION_SCORE = 70
const DEFAULT_WEIGHT = 1
const MAX_WEIGHT = 10

const METHOD_HELP = {
  direct: 'One holistic score for the album, independent of your track ratings.',
  track_average:
    'Your album score follows the mean of your track ratings, and moves when you rate another track. Your holistic score is kept.',
  blended:
    'Your holistic score is the album score; your track average sits beside it. Both are kept.',
}

export default function RatingSheet({
  title,
  subtitle = '',
  artwork = null,
  variant = 'album',
  initialScore = null,
  initialMethod = 'direct',
  initialState = null,
  initialVisibility = 'public',
  initialSubscores = null,
  initialSubscoreMode = 'independent',
  initialSubscoreWeights = null,
  initialTags = null,
  trackAverage = null,
  canRemove = false,
  busy = false,
  error = null,
  onSave,
  onRemove,
  onSuggestTag = null,
  onClose,
}) {
  const [score, setScore] = useState(initialScore ?? DEFAULT_SCORE)
  // What is in the exact-score box while somebody is still typing it. `null`
  // means "show the score", which is what every other control writes to.
  const [draft, setDraft] = useState(null)
  const [method, setMethod] = useState(initialMethod)
  const [state, setState] = useState(initialState)
  const [visibility, setVisibility] = useState(initialVisibility)
  // Notes live on revisions, not on the rating, so the box starts empty every
  // time: each save that changes something writes this note against *that*
  // change, and yesterday's reasoning stays in the timeline where it happened.
  const [note, setNote] = useState('')

  // Sub-scores (1B-4b Phase 1). `subscores` holds only the dimensions somebody
  // has actually filled in — a dimension absent from this object is absent
  // from the request, which is how the "missing is not zero" invariant reaches
  // the wire without anybody having to remember it.
  const [subscores, setSubscores] = useState(() => ({ ...(initialSubscores || {}) }))
  const [subscoreMode, setSubscoreMode] = useState(initialSubscoreMode || 'independent')
  const [weights, setWeights] = useState(() => ({ ...(initialSubscoreWeights || {}) }))
  // The vocabulary, fetched rather than hardcoded. `null` while it is on its
  // way; `[]` if the request failed, which folds the section away and leaves
  // the rest of the sheet working — an optional feature should not be able to
  // stop somebody rating an album.
  const [dimensions, setDimensions] = useState(null)

  // Tags (1B-4b Phase 2a). The slugs that are ticked, and the curated
  // vocabulary they are ticked from — grouped by family by the server, so this
  // holds no opinion about which families exist or what order they go in.
  // `[]` on a failed fetch folds the section away, same as the dimensions.
  const [tags, setTags] = useState(() => [...(initialTags || [])])
  const [tagFamilies, setTagFamilies] = useState(null)

  // Suggesting a word (1B-4b Phase 2b). `suggested` holds the word the server
  // accepted, which is what the acknowledgement echoes — the server normalizes,
  // and showing what it stored rather than what was typed is what stops two
  // people from thinking they asked for two different things.
  const [suggestion, setSuggestion] = useState('')
  const [suggesting, setSuggesting] = useState(false)
  const [suggested, setSuggested] = useState('')
  const [suggestError, setSuggestError] = useState('')

  // Escape, the Tab cycle and returning focus to whatever opened this all
  // live in one hook now, shared with the composer — see `lib/focusTrap.js`.
  const dialogRef = useFocusTrap(true, onClose)

  const isAlbum = variant === 'album'
  const wantsDimensions = variant !== 'track'
  // Same rule as the dimensions: a track rating is one number in a list and
  // has no room for either. Nothing about §5.15 is album-only, so an album and
  // a song get the same words.
  const wantsTags = variant !== 'track'

  useEffect(() => {
    if (!wantsDimensions) return undefined
    const controller = new AbortController()
    api
      .ratingDimensions({ signal: controller.signal })
      .then((body) => setDimensions(isAlbum ? body.album || [] : body.song || []))
      .catch(() => setDimensions([]))
    return () => controller.abort()
  }, [wantsDimensions, isAlbum])

  useEffect(() => {
    if (!wantsTags) return undefined
    const controller = new AbortController()
    api
      .ratingTags({ signal: controller.signal })
      .then((body) => setTagFamilies(body.families || []))
      .catch(() => setTagFamilies([]))
    return () => controller.abort()
  }, [wantsTags])
  const averageReady = Boolean(trackAverage?.available)
  const shown = display(score)
  const field = draft ?? shown
  const invalid = draft !== null && parse(draft) === null
  const invalidId = useId()

  /**
   * Stepping is a *functional* update, deliberately.
   *
   * `setScore(score + 1)` reads the score this render closed over, so ten
   * clicks inside one frame all compute the same number and nine of them do
   * nothing. Somebody moving from 7.0 to 8.0 clicks exactly that fast — found
   * by doing it in a browser, not by reading this.
   */
  function step(delta) {
    setDraft(null)
    setScore((current) => clamp(current + delta))
  }

  function slide(next) {
    setDraft(null)
    setScore(clamp(next))
  }

  /** Add a dimension at a starting value, or clear it back to unscored. */
  function setDimension(slug, value) {
    setSubscores((current) => {
      const next = { ...current }
      if (value === null) delete next[slug]
      else next[slug] = clamp(value)
      return next
    })
    if (value === null) {
      setWeights((current) => {
        const next = { ...current }
        delete next[slug]
        return next
      })
    }
  }

  function toggleTag(slug) {
    setTags((current) =>
      current.includes(slug) ? current.filter((each) => each !== slug) : [...current, slug],
    )
  }

  /**
   * Send a word to whoever keeps the list.
   *
   * Deliberately not part of `submit`: a suggestion is not part of the rating
   * and must not ride on saving one, or closing the sheet without saving would
   * throw the suggestion away and saving twice would send it twice. It also
   * cannot become a tag here — the input is never written into `tags`, because
   * the server would refuse the slug and the person would have watched a chip
   * appear and then vanish.
   */
  async function sendSuggestion() {
    const text = suggestion.trim()
    if (!text || suggesting || !onSuggestTag) return
    setSuggesting(true)
    setSuggestError('')
    try {
      const body = await onSuggestTag(text)
      setSuggested(body?.text || text)
      setSuggestion('')
    } catch (problem) {
      // The API's sentence, then the sheet's. `problem?.message` alone reads
      // the browser's "Failed to fetch" out loud when `fetch` itself rejects.
      setSuggestError(problem instanceof ApiError ? problem.message : 'That did not go through.')
    } finally {
      setSuggesting(false)
    }
  }

  /**
   * Every dimension the server knows, sent as a score or as `null`.
   *
   * `null` is what clears one. Sending only the filled-in dimensions would
   * leave a cleared dimension untouched — the API treats an absent key as
   * "leave it alone", deliberately, so that saving a note does not wipe eight
   * of them. The sheet knows the person just looked at all of them, so it is
   * the one caller that can say what every dimension should now be.
   */
  function subscorePayload() {
    if (!dimensions?.length) return undefined
    return Object.fromEntries(dimensions.map((slug) => [slug, subscores[slug] ?? null]))
  }

  function submit(event) {
    event.preventDefault()
    // Enter in the exact-score field submits too; a draft that is not a score
    // must not save whatever prefix of it last parsed (Q15).
    if (busy || invalid) return
    if (variant === 'track') return onSave({ score })
    const depth = {
      subscores: subscorePayload(),
      subscore_mode: subscoreMode,
      subscore_weights: subscoreMode === 'weighted' ? weights : undefined,
      // The list sent *is* the tags afterwards, so an emptied picker has to
      // send `[]` rather than nothing. A sheet that never drew the picker has
      // to do the opposite and send nothing: `[]` there would untick, on save,
      // tags the person was never shown. The condition is "was it drawn", not
      // "did the request finish" — an empty array is truthy, and a vocabulary
      // that failed to load and one that came back empty fold away the same.
      tags: tagFamilies?.length ? tags : undefined,
    }
    if (variant === 'song') return onSave({ score, state, visibility, note, ...depth })
    return onSave({ score, method, state, visibility, note, ...depth })
  }

  return (
    <div
      className="sheet-backdrop"
      onClick={(event) => {
        if (event.target === event.currentTarget) onClose()
      }}
    >
      {/*
        The dialog is the div and the form is inside it. `role="dialog"` on a
        `<form>` is invalid — a named form already has the `form` role and ARIA
        in HTML does not allow overriding it — which axe reports as
        `aria-allowed-role`. Found by putting axe in the gate, not by reading.
      */}
      <div
        ref={dialogRef}
        className="sheet"
        role="dialog"
        aria-modal="true"
        aria-label={`Rate ${title}`}
      >
        <form onSubmit={submit} className="sheet-form">
          <div className="sheet-grabber" aria-hidden="true" />

          <div className="sheet-head">
            {artwork ? <Artwork art={artwork} title={title} artist={subtitle} size={52} /> : null}
            <div className="row-main">
              <div className="row-title">{title}</div>
              {subtitle ? <div className="row-sub">{subtitle}</div> : null}
            </div>
            <SheetClose onClose={onClose} />
          </div>

          <div className="sheet-section">
            <div className="score-readout">
              <span className="value num">{shown}</span>
              <span className="of">/ 10</span>
            </div>
            <div className="slider-row">
              <button
                type="button"
                className="stepper"
                onClick={() => step(-1)}
                aria-label="Decrease by one tenth"
              >
                −
              </button>
              <input
                type="range"
                className="score-slider"
                min={SCORE_MIN}
                max={SCORE_MAX}
                step="1"
                value={score}
                style={{ '--fill-pct': `${score}%` }}
                onChange={(event) => slide(Number(event.target.value))}
                aria-label={`${title} score`}
                aria-valuetext={`${shown} out of 10`}
              />
              <button
                type="button"
                className="stepper"
                onClick={() => step(1)}
                aria-label="Increase by one tenth"
              >
                +
              </button>
              <input
                type="text"
                inputMode="decimal"
                className="score-exact num"
                value={field}
                onChange={(event) => {
                  setDraft(event.target.value)
                  const parsed = parse(event.target.value)
                  if (parsed !== null) setScore(parsed)
                }}
                // A valid draft becomes the score and goes; an invalid one
                // stays with its sentence (Q15). Dropping it on blur is how
                // 10.1 used to read back as 10.0 with nothing said.
                onBlur={() => {
                  if (!invalid) setDraft(null)
                }}
                aria-label="Exact score"
                aria-invalid={invalid || undefined}
                aria-describedby={invalid ? invalidId : undefined}
              />
            </div>
            {invalid ? (
              <p id={invalidId} className="field-error" role="alert">
                A score runs from 0.0 to 10.0, in tenths.
              </p>
            ) : null}
          </div>

          {isAlbum ? (
            <div className="sheet-section">
              <span className="sheet-label" id="method-label">
                Album score method
              </span>
              <div className="segmented" role="group" aria-labelledby="method-label">
                {METHODS.map((option) => (
                  <button
                    key={option}
                    type="button"
                    aria-pressed={method === option}
                    disabled={option === 'track_average' && !averageReady}
                    onClick={() => setMethod(option)}
                  >
                    {METHOD_LABELS[option]}
                  </button>
                ))}
              </div>
              <p className="mode-help">
                {METHOD_HELP[method]}
                {!averageReady && method !== 'track_average' ? (
                  <>
                    {' '}
                    <span className="faint">{coverageLabel(trackAverage)}.</span>
                  </>
                ) : null}
              </p>
              {method !== 'direct' ? (
                <div className="mode-figures">
                  <div className="mode-figure">
                    <div className="v num">{display(trackAverage?.value) ?? '—'}</div>
                    <div className="k">Track average · {coverageLabel(trackAverage)}</div>
                  </div>
                  {method === 'blended' ? (
                    <div className="mode-figure">
                      <div className="v num">{shown}</div>
                      <div className="k">Your holistic score</div>
                    </div>
                  ) : null}
                </div>
              ) : null}
            </div>
          ) : null}

          {wantsDimensions && dimensions?.length ? (
            <div className="sheet-section subscores">
              <span className="sheet-label" id="subscore-label">
                Detailed sub-scores <span className="sheet-label-aside">(optional)</span>
              </span>

              <div className="segmented" role="group" aria-labelledby="subscore-label">
                {SUBSCORE_MODES.map((option) => (
                  <button
                    key={option}
                    type="button"
                    aria-pressed={subscoreMode === option}
                    onClick={() => setSubscoreMode(option)}
                  >
                    {SUBSCORE_MODE_LABELS[option]}
                  </button>
                ))}
              </div>
              <p className="mode-help">{SUBSCORE_MODE_HELP[subscoreMode]}</p>

              <ul className="subscore-list">
                {dimensions.map((slug) => {
                  const value = subscores[slug]
                  const scored = value != null
                  const label = dimensionLabel(slug)
                  return (
                    <li key={slug} className="subscore-row">
                      <span className="subscore-name">{label}</span>
                      {scored ? (
                        <>
                          <input
                            type="range"
                            className="score-slider subscore-slider"
                            min={SCORE_MIN}
                            max={SCORE_MAX}
                            step="1"
                            value={value}
                            style={{ '--fill-pct': `${value}%` }}
                            onChange={(event) => setDimension(slug, Number(event.target.value))}
                            aria-label={`${label} score`}
                            aria-valuetext={`${display(value)} out of 10`}
                          />
                          <span className="subscore-value num">{display(value)}</span>
                          {subscoreMode === 'weighted' ? (
                            <span className="subscore-weight">
                              <input
                                type="number"
                                className="field-input"
                                min={0}
                                max={MAX_WEIGHT}
                                step="1"
                                value={weights[slug] ?? DEFAULT_WEIGHT}
                                onChange={(event) =>
                                  setWeights((current) => ({
                                    ...current,
                                    [slug]: clamp(Number(event.target.value)),
                                  }))
                                }
                                aria-label={`${label} weight`}
                              />
                              {/* A `<label>` wrapping this would take the ×
                                  into the accessible name. `aria-label` on the
                                  input says exactly what it is. */}
                              <span aria-hidden="true">&times;</span>
                            </span>
                          ) : null}
                          <button
                            type="button"
                            className="btn btn-quiet btn-sm"
                            onClick={() => setDimension(slug, null)}
                          >
                            Clear
                          </button>
                        </>
                      ) : (
                        <>
                          {/*
                            "Not scored" is a state, not a zero. A dimension
                            nobody filled in has no row on the server and is
                            excluded from every average — so the sheet has to
                            offer a way *into* a dimension rather than starting
                            everything at some number nobody chose.
                          */}
                          <span className="subscore-empty footnote">Not scored</span>
                          <button
                            type="button"
                            className="btn btn-quiet btn-sm"
                            onClick={() => setDimension(slug, DEFAULT_DIMENSION_SCORE)}
                          >
                            Add {label.toLowerCase()}
                          </button>
                        </>
                      )}
                    </li>
                  )
                })}
              </ul>
            </div>
          ) : null}

          {wantsTags && tagFamilies?.length ? (
            <div className="sheet-section tags">
              <span className="sheet-label" id="tag-label">
                Tags <span className="sheet-label-aside">(optional)</span>
              </span>
              <p className="mode-help">
                Words for what this is and how you heard it. They are yours, and they are as
                private as the rating they sit on.
              </p>

              {tagFamilies.map((group) => (
                <div key={group.family} className="tag-family" role="group" aria-label={familyLabel(group.family)}>
                  <span className="tag-family-name footnote">{familyLabel(group.family)}</span>
                  <ul className="tag-list">
                    {group.tags.map((tag) => (
                      <li key={tag.slug}>
                        {/*
                          A button with `aria-pressed`, not a checkbox: this is a
                          chip somebody toggles, and the pressed state is what a
                          screen reader should hear. The same pattern as the
                          method and mode selectors above it.
                        */}
                        <button
                          type="button"
                          className="tag-chip"
                          aria-pressed={tags.includes(tag.slug)}
                          onClick={() => toggleTag(tag.slug)}
                        >
                          {tag.label}
                        </button>
                      </li>
                    ))}
                  </ul>
                </div>
              ))}

              {onSuggestTag ? (
                <div className="tag-suggest">
                  <label className="footnote" htmlFor="tag-suggestion">
                    Missing a word?
                  </label>
                  <p className="mode-help">
                    Suggestions go to the person who keeps this list. They are not tags yet, and
                    nothing is added to your rating.
                  </p>
                  <div className="tag-suggest-row">
                    <input
                      id="tag-suggestion"
                      type="text"
                      maxLength={60}
                      value={suggestion}
                      placeholder="shoegazey"
                      onChange={(event) => {
                        setSuggestion(event.target.value)
                        setSuggestError('')
                        setSuggested('')
                      }}
                      onKeyDown={(event) => {
                        // Enter suggests the word rather than saving the
                        // rating. Without this the form's submit handler wins
                        // and the sheet closes on somebody who was still
                        // typing a suggestion.
                        if (event.key === 'Enter') {
                          event.preventDefault()
                          sendSuggestion()
                        }
                      }}
                    />
                    <button
                      type="button"
                      className="ghost"
                      disabled={suggesting || !suggestion.trim()}
                      onClick={sendSuggestion}
                    >
                      Suggest
                    </button>
                  </div>
                  {suggested ? (
                    <p className="footnote" role="status">
                      Thanks — “{suggested}” is with the person who keeps the list.
                    </p>
                  ) : null}
                  {suggestError ? (
                    <p className="form-error" role="alert">
                      {suggestError}
                    </p>
                  ) : null}
                </div>
              ) : null}
            </div>
          ) : null}

          {variant !== 'track' ? (
            <>
              <div className="sheet-section">
                <span className="sheet-label" id="state-label">
                  Where is this opinion? <span className="sheet-label-aside">(optional)</span>
                </span>
                <div className="chip-row" role="group" aria-labelledby="state-label">
                  {STATES.map((option) => (
                    <button
                      key={option}
                      type="button"
                      className="chip"
                      aria-pressed={state === option}
                      onClick={() => setState(state === option ? null : option)}
                    >
                      {STATE_LABELS[option]}
                    </button>
                  ))}
                </div>
              </div>

              <div className="sheet-section">
                <label className="sheet-label" htmlFor="rating-note">
                  Note <span className="sheet-label-aside">(optional)</span>
                </label>
                <textarea
                  id="rating-note"
                  className="composer-body rating-note"
                  value={note}
                  onChange={(event) => setNote(event.target.value)}
                  rows={2}
                  maxLength={500}
                  placeholder="Why this score?"
                />
              </div>

              <div className="sheet-section">
                <div className="form-row p-0">
                  <div className="row-main">
                    <div className="row-title">Keep this private</div>
                    <div className="row-sub wrap" id="private-rating-help">
                      Yours to see. Left out of the community score and the histogram.
                    </div>
                  </div>
                  <Toggle
                    checked={visibility === 'private'}
                    onChange={(next) => setVisibility(next ? 'private' : 'public')}
                    label="Keep this private"
                    describedBy="private-rating-help"
                  />
                </div>
              </div>
            </>
          ) : null}

          {error ? (
            <p className="form-alert" role="alert">
              {error}
            </p>
          ) : null}

          <button
            type="submit"
            className="btn btn-primary sheet-submit"
            disabled={busy || invalid}
          >
            {busy ? 'Saving…' : 'Save rating'}
          </button>
          {canRemove ? (
            <button
              type="button"
              className="btn btn-quiet btn-block sheet-remove"
              onClick={onRemove}
              disabled={busy}
            >
              Remove rating
            </button>
          ) : null}
        </form>
      </div>
    </div>
  )
}
